import * as React from "react";

import { useQuery } from "react-query";
import * as Redux from "react-redux";

import {
  FormCompletion,
  initialState as intialFormCompletionState,
} from "client/domain/Onboarding/formCompletionSlice";
import { useFeatureFlags, useRestore } from "client/hooks";
import { logAmplitudeEvent } from "client/lib/amplitude";
import {
  CashPayOnboardingStatus,
  CountryList,
  OnboardingGroup,
  OnboardingStatus as OnboardingStatusType,
  OpportunityStage,
  SalesChannel,
} from "client/lib/apt-merchant";
import { OnboardingStep } from "client/pages/OnboardingStatus/utils";
import { UserService, ZdoService } from "client/services";
import {
  GetCashPayEligibilityResponse,
  OpportunityStageResponse,
} from "client/services/user";
import { CredentialResponse } from "client/services/zdo";
import { errorActions, RootState } from "client/store";
import { getClientId, isRunningJest } from "client/utils";

export type OnboardingStatusContext = {
  cashPayIntegrationComplete: boolean;
  cashPayOnboardingStatus: CashPayOnboardingStatus | undefined;
  customizeCashPayProfileComplete: boolean;
  sidebarVisableStep: OnboardingStep | undefined;
  formCompletionState: FormCompletion;
  getApcCredentialsResponse: CredentialResponse | undefined;
  isEligibleForCashPay: boolean;
  isEligibleForInStoreSelfServeActivation: boolean;
  inStoreDirectDebitAuthorized: boolean;
  inStoreTrainingButtonClicked: boolean;
  inStoreTrainingComplete: boolean;
  merchantId: string | undefined;
  messagingComplete: boolean;
  multiTerminal: string;
  onAddMessagingComplete: () => void;
  onboardingGroup: OnboardingGroup;
  onboardingStatus: OnboardingStatusType;
  onTrainingButtonClicked: () => void;
  onActiveOnboardingStepUpdated: (step?: OnboardingStep) => void;
  onCustomizeCashPayProfileComplete: () => void;
  toggleCashPayIntegrationComplete: () => void;
  opportunityStage: OpportunityStage;
  registeredCountry: CountryList;
  salesChannel: SalesChannel;
  zdoEligibilityPolling: boolean;
};

const initialContextState: OnboardingStatusContext = {
  cashPayIntegrationComplete: false,
  cashPayOnboardingStatus: "NONE",
  customizeCashPayProfileComplete: false,
  sidebarVisableStep: undefined,
  formCompletionState: intialFormCompletionState,
  getApcCredentialsResponse: undefined,
  isEligibleForCashPay: false,
  isEligibleForInStoreSelfServeActivation: false,
  inStoreDirectDebitAuthorized: false,
  inStoreTrainingComplete: false,
  inStoreTrainingButtonClicked: false,
  messagingComplete: false,
  merchantId: undefined,
  multiTerminal: "No",
  opportunityStage: "Compliance Checks",
  onAddMessagingComplete: () => {
    /*Blank*/
  },
  onboardingGroup: "Normal Merchant",
  onboardingStatus: "NO_RECORD",
  onTrainingButtonClicked: () => {
    /*Blank*/
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onActiveOnboardingStepUpdated: () => {
    /*Blank*/
  },
  onCustomizeCashPayProfileComplete: () => {
    /*Blank*/
  },
  toggleCashPayIntegrationComplete: () => {
    /*Blank*/
  },
  registeredCountry: "United States",
  salesChannel: "Online Only",
  zdoEligibilityPolling: false,
};

export const StatusContext = React.createContext<OnboardingStatusContext>(
  initialContextState
);

const determineInstoreEligibility = ({
  selfServeInStore,
}: {
  selfServeInStore: boolean;
}): boolean => selfServeInStore;

const MAX_RETRY_COUNT = 3;

const clientId = getClientId();

export const StatusContextProvider: React.FC = ({ children }) => {
  const dispatch = Redux.useDispatch();
  const { refetchRestore, userRestored } = useRestore();
  const initial = React.useRef<boolean>(true);
  const retryCount = React.useRef(0);

  const [
    { merchantPortalReleaseCashPay, merchantPortalReleaseCashPayFeatureSet2 },
  ] = useFeatureFlags();

  const [zdoEligibilityPolling, setZdoEligibilityPolling] = React.useState(
    false
  );
  const [messagingComplete, setMessagingComplete] = React.useState<boolean>(
    Boolean(localStorage?.getItem("messagingComplete"))
  );
  const [
    opportunityStage,
    setOpportunityStage,
  ] = React.useState<OpportunityStage>("Compliance Checks");

  const [
    trainingButonClicked,
    setTrainingButtonClicked,
  ] = React.useState<boolean>(false);

  const [
    customizeCashPayProfileComplete,
    setCustomizeCashPayProfileComplete,
  ] = React.useState<boolean>(false);

  const [
    cashPayIntegrationComplete,
    setCashPayIntegrationComplete,
  ] = React.useState<boolean>(false);

  const [sidebarVisableStep, setSidebarVisableStep] = React.useState<
    OnboardingStep | undefined
  >(undefined);

  const {
    formCompletion,
    merchant: {
      merchantAccount: {
        cashPayOnboardingStatus,
        inStoreDirectDebitAuthorized,
        inStoreTrainingComplete,
        merchantId,
        onboardingGroup,
        onboardingStatus,
        mlsProspectRef,
        selfServeInStore,
      },
      fieldValueMap: {
        lead: { registeredCountry, salesChannel },
        storeDetails: { multiTerminal },
      },
    },
  } = Redux.useSelector(({ formCompletion, merchant }: RootState) => ({
    formCompletion,
    merchant,
  }));

  const [
    isEligibleForInStoreSelfServeActivation,
    setIsEligibleForInStoreSelfServeActivation,
  ] = React.useState<boolean>(
    determineInstoreEligibility({
      selfServeInStore,
    })
  );

  const [
    isEligibleForCashPay,
    setIsEligibleForCashPay,
  ] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (retryCount.current > MAX_RETRY_COUNT) {
      // onboarding status is perpetually failing, so we'll set a default and log an error
      if (!isRunningJest()) {
        console.error(
          `Onboarding status is failing to update after 3 retries for ${clientId}:clientId ${mlsProspectRef}:mlsProspectRef`
        );
      }
      statusContextValue.onboardingStatus = getFallbackOnboardingStatus(
        onboardingGroup
      );
    }
    if (!onboardingStatus) {
      if (!isRunningJest()) {
        console.error(
          `Onboarding status is undefined for ${clientId}:clientId ${mlsProspectRef}:mlsProspectRef`
        );
      }
    } else if (onboardingStatus === "IN_PROGRESS" && retryCount.current < 3) {
      if (!isRunningJest()) {
        console.error(
          `Onboarding status is IN_PROGRESS for ${clientId}:clientId, ${mlsProspectRef}:mlsProspectRef and ${retryCount.current}:retryCount`
        );
      }
      setTimeout(() => {
        refetchRestore();
        retryCount.current++;
      }, 3000);
    } else {
      retryCount.current = 0;
    }
  }, [onboardingStatus, retryCount]);

  useQuery<OpportunityStageResponse, Error>(
    "opp-stage",
    () => UserService.getOpportunityStage(),
    {
      enabled:
        salesChannel === "Online and In-store" ||
        salesChannel === "In-store Only",
      onSuccess: ({ opportunityStage }) => {
        setOpportunityStage(opportunityStage);
      },
      onError: (err: Error) => {
        dispatch(errorActions.setError(err.message));
      },
    }
  );

  const { data: createApcResponse } = useQuery<
    ZdoService.CreateApcResponse,
    Error
  >(
    ["create-apc-account", merchantId, onboardingGroup],
    ZdoService.createApcAccount,
    {
      enabled:
        !zdoEligibilityPolling &&
        userRestored &&
        !merchantId &&
        !onboardingGroup,
      onSuccess: ({ status }) => {
        // Here when the status is "CREATED" for ZDO we need to
        // check and see if the merchant is eligible for ZDO hense
        // the need to refetch details from restore on the merchnt
        // to see what onboarding group/ onboarding status is now
        // that we have an APC account created
        if (status === "PENDING") {
          setZdoEligibilityPolling(true);
        } else {
          logAmplitudeEvent(
            "ZDO_ELIGIBILITY",
            status === "CREATED" ? "user_eligible" : "user_ineligible"
          );
          refetchRestore();
        }
      },
    }
  );

  const { data: getApcCredentialsResponse } = useQuery<
    ZdoService.CredentialResponse,
    Error
  >("get-apc-credentials", ZdoService.getApcCredentials, {
    enabled: Boolean(merchantId) || createApcResponse?.status === "CREATED",
  });

  useQuery<GetCashPayEligibilityResponse, Error>(
    ["check-cash-pay-eligibility"],
    () => UserService.checkCashPayEligibility(),
    {
      enabled:
        Boolean(merchantId) &&
        merchantPortalReleaseCashPayFeatureSet2 &&
        merchantPortalReleaseCashPay,
      onSuccess: ({ eligible }) => {
        setIsEligibleForCashPay(eligible);
      },
      onError: (err: Error) => {
        dispatch(errorActions.setError(err.message));
      },
    }
  );

  if (initial.current) {
    initial.current = false;
  }

  const getFallbackOnboardingStatus: (
    onboardingGroup?: OnboardingGroup
  ) => OnboardingStatusType = (onboardingGroup) =>
    onboardingGroup === "ZDO Merchant" || onboardingGroup === "ZDO Pilot V1"
      ? "READY_TO_INSTALL"
      : "IN_REVIEW";

  React.useEffect(() => {
    setIsEligibleForInStoreSelfServeActivation(
      determineInstoreEligibility({
        selfServeInStore,
      })
    );
  }, [selfServeInStore]);
  React.useEffect(() => {
    refetchRestore();
  }, [formCompletion.cashPayForm, , cashPayOnboardingStatus]);

  const onAddMessagingComplete = () => {
    setMessagingComplete(true);
    localStorage?.setItem("messagingComplete", "true");
  };

  const onCustomizeCashPayProfileComplete = () => {
    setCustomizeCashPayProfileComplete(true);
  };

  const toggleCashPayIntegrationComplete = () => {
    if (cashPayIntegrationComplete) {
      setCashPayIntegrationComplete(false);
    } else {
      setCashPayIntegrationComplete(true);
    }
  };

  const statusContextValue = {
    cashPayIntegrationComplete,
    cashPayOnboardingStatus: cashPayOnboardingStatus ?? "NONE",
    customizeCashPayProfileComplete,
    sidebarVisableStep,
    formCompletionState: formCompletion,
    getApcCredentialsResponse,
    isEligibleForCashPay,
    isEligibleForInStoreSelfServeActivation,
    inStoreDirectDebitAuthorized: Boolean(inStoreDirectDebitAuthorized),
    inStoreTrainingButtonClicked: trainingButonClicked,
    inStoreTrainingComplete: Boolean(inStoreTrainingComplete),
    merchantId,
    multiTerminal,
    messagingComplete,
    opportunityStage,
    onAddMessagingComplete,
    onboardingGroup: onboardingGroup ?? "Normal Merchant",
    onboardingStatus:
      onboardingStatus ?? getFallbackOnboardingStatus(onboardingGroup),
    onTrainingButtonClicked: () => setTrainingButtonClicked(true),
    onActiveOnboardingStepUpdated: setSidebarVisableStep,
    onCustomizeCashPayProfileComplete,
    toggleCashPayIntegrationComplete,
    registeredCountry,
    salesChannel,
    zdoEligibilityPolling,
  };

  return (
    <StatusContext.Provider value={statusContextValue}>
      {children}
    </StatusContext.Provider>
  );
};
